<template>
  <div class="pt-[60px] xl:pt-[120px] px-4 xl:px-[15%]">
    <div>
      <h1 class="text-3xl font-bold mb-6">
        {{ i18n.t('marketplace.items_index.title')  }}
      </h1>
    </div>
    <SearchDestination class="w-full" :emitChanges="true" :profile="profile" @change="setLocationAndDates" />
    
    <!-- Type Selector: Rentals / Experiences -->
    <div class="flex items-center gap-1 bg-gray-100 rounded-lg font-bold text-sm mt-4 w-fit">
      <div class="py-1 px-4 rounded-lg text-gray-500 cursor-pointer grow text-center"
            @click="activeType = 'rentals'; applyFilters()"
           :class="{'bg-[#86ECFF] !text-black shadow': activeType === 'rentals'}">
        <img src="/images/marketplace/rentals.png" alt="Rentals" class="inline w-8 mr-1" />
        {{ i18n.t('marketplace.items_index.rentals') }}
      </div>
      <div class="py-1 px-4 rounded-lg text-gray-500 cursor-pointer grow text-center"
            @click="activeType = 'experiences'; activeSubCategory = 'all'; applyFilters()"
           :class="{'bg-[#86ECFF] !text-black shadow': activeType === 'experiences'}">
        <img src="/images/marketplace/experiences.png" alt="Experiences" class="inline w-8 mr-1" />
        {{ i18n.t('marketplace.items_index.experiences') }}
      </div>
    </div>
    
    <!-- Categories with Icons -->
    <div class="relative">
      <!-- Left arrow button -->
      <button v-if="showLeftScroll" 
              @click="scrollCategories('left')"
              class="absolute left-1 top-10 z-20 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors arrow-button">
        <i class="fa fa-chevron-left text-gray-700"></i>
      </button>
      
      <!-- Right arrow button -->
      <button v-if="showRightScroll" 
              @click="scrollCategories('right')"
              class="absolute right-1 top-10 z-20 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors arrow-button">
        <i class="fa fa-chevron-right text-gray-700"></i>
      </button>
      
      <!-- Left scroll indicator -->
      <div v-if="showLeftScroll" 
           class="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none hidden md:block"></div>
      
      <!-- Right scroll indicator -->
      <div v-if="showRightScroll" 
           class="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none hidden md:block"></div>
      
      <!-- Categories scroll container -->
      <div ref="categoriesContainer" 
           @scroll="handleCategoriesScroll"
           class="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide snap-x snap-mandatory md:snap-none"
           style="scroll-behavior: smooth; -webkit-overflow-scrolling: touch;">
        <!-- Dynamic Categories -->
        <div v-for="category in availableCategories" :key="category.id"
             class="flex flex-col items-center cursor-pointer shrink-0 min-w-[80px] snap-start"
             @click="activeCategory = category.id; activeSubCategory = 'all'; applyFilters()">
          <div class="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-2 p-2"
               :class="{'!bg-[#86ECFF]': activeCategory == category.id}">
            <img v-if="category.icon_url" :src="category.icon_url" :alt="category.label" class="w-full h-full object-contain" />
            <i v-else :class="[getCategoryIcon(category.label), activeCategory == category.id ? 'text-black' : 'text-gray-600']" class="text-3xl"></i>
          </div>
          <span class="text-xs font-semibold text-center" :class="activeCategory == category.id ? 'text-black' : 'text-gray-600'">
            {{ category.label }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Cart restriction notification banner -->
    <div v-if="cart && cart.items_count > 0" class="mb-6 bg-[#2c2d30] text-white rounded-xl px-6 py-3 border border-gray-700">
      <div class="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
        <div class="flex-1">
          <p class="text-base font-medium">
            <b>{{ i18n.t('marketplace.items_index.showing_items_from') }} {{ cart.profile_name }}.</b><br>
            {{ i18n.t('marketplace.items_index.clear_cart_to_browse') }}
          </p>
        </div>
        <button 
          @click="clearCart"
          class="px-6 py-3 rounded-lg font-semibold whitespace-nowrap bg-white text-gray-900 hover:bg-gray-100 transition-colors">
          {{ i18n.t('marketplace.items_index.clear_cart') }}
        </button>
      </div>
    </div>
    
    <!-- Show all rentals button when viewing single item -->
    <div v-if="showingSingleItem && specificItemId" class="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
      <div class="flex flex-col xl:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <div class="bg-blue-100 rounded-full p-3">
            <i class="bi bi-grid-3x3-gap text-blue-600 text-2xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-mk-dark-blue">{{ i18n.t('marketplace.items_index.viewing_single_item') }}</h3>
            <p class="text-sm text-gray-600">{{ i18n.t('marketplace.items_index.see_all_in_location') }}</p>
          </div>
        </div>
        <button 
          @click="showAllRentals"
          class="jt-btn jt-mk-btn px-6 py-3 rounded-lg font-semibold whitespace-nowrap">
          {{ i18n.t('marketplace.items_index.show_all_rentals') }}
        </button>
      </div>
    </div>
    
    <div class="xl:flex items-center gap-4 mb-6">
      <div v-if="activeCategory" class="relative flex-1 xl:max-w-[60vw]">
        <!-- Left arrow button for subcategories -->
        <button v-if="showLeftScrollSub" 
                @click="scrollSubcategories('left')"
                class="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors arrow-button">
          <i class="fa fa-chevron-left text-gray-700 text-xs"></i>
        </button>
        
        <!-- Right arrow button for subcategories -->
        <button v-if="showRightScrollSub" 
                @click="scrollSubcategories('right')"
                class="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors arrow-button">
          <i class="fa fa-chevron-right text-gray-700 text-xs"></i>
        </button>
        
        <!-- Left scroll indicator for subcategories -->
        <div v-if="showLeftScrollSub" 
             class="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none hidden md:block"></div>
        
        <!-- Right scroll indicator for subcategories -->
        <div v-if="showRightScrollSub" 
             class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none hidden md:block"></div>
        
        <div ref="subcategoriesContainer" 
             @scroll="handleSubcategoriesScroll"
             class="flex items-center gap-2 overflow-x-auto py-1 scrollbar-hide"
             :class="{
               'md:pl-10': showLeftScrollSub,
               'md:pr-10': showRightScrollSub
             }">
          <div class="py-1 px-3 rounded-full border cursor-pointer text-xs shrink-0"
              :class="{' !bg-[#86ECFF] font-semibold': activeSubCategory == 'all'}"
              @click="activeSubCategory = 'all'; applyFilters()">
              All {{ activeCategory.label }}
          </div>
          <div v-for="category in availableSubCategories" :key="category.id"
              class="py-1 px-3 rounded-full border cursor-pointer text-xs shrink-0"
              @click="activeSubCategory = category.id; applyFilters()"
              :class="{' !bg-[#86ECFF] font-semibold': activeSubCategory == category.id}"
          >
            {{ category.label }}
          </div>
        </div>
      </div>
      <div class="flex items-center gap-1 ml-auto mt-4 xl:mt-0">
        <h4 class="text-xl font-bold truncate inline xl:hidden">{{ activeCategoryLabel }}</h4>
        <div class=" inline-block relative bg-gray-100 ml-auto rounded-lg">
          <input type="text" v-model="searchQuery" @change="applyFilters"
                :class="{'!w-56': searchQuery.length > 0}"
                class="rounded-lg focus:w-56 focus:pr-10 px-3 cursor-pointer py-2 w-10 h-10 border bg-transparent relative z-10" />
          <div class="absolute top-0 right-0 flex items-center justify-center w-10 h-10 rounded-lg ">
            <i class="fa fa-search"></i>
          </div>
        </div>
        <div class="relative">
          <div class="rounded-lg bg-gray-100 border cursor-pointer w-10 h-10 flex items-center justify-center" @click="openFilter">
            <i class="fa fa-filter"></i>
          </div>
          <div v-if="openFilterFlyout" class="fixed bg-black opacity-5 top-0 left-0 w-full h-full z-50" @click="openFilterFlyout = false"></div>
          <div v-if="openFilterFlyout" class="fixed top-0 right-0 bg-white shadow-lg h-full p-4 w-80 z-50">
            <div class="flex items-center justify-between mb-4">
              <h4 class="font-semibold text-lg mb-2">{{ i18n.t('marketplace.items_index.filters') }}</h4>
              <button class="text-gray-500 hover:text-gray-700" @click="openFilterFlyout = false">
                <i class="fa fa-times"></i>
              </button>
            </div>
            <div class="flex flex-col gap-6 overflow-y-auto h-[calc(100vh-200px)]">
              <div>
                <h5 class="font-semibold mb-2">{{ i18n.t('marketplace.items_index.sort') }}</h5>
                <div v-for="option in sorting" :key="option.value"
                    class="cursor-pointer font-medium py-2 px-3 mb-2 rounded-lg bg-gray-100 border-solid border-2 border-gray-100"
                    @click="activeSorting = option.value; "
                    :class="{'!bg-white !border-black': activeSorting === option.value}">
                    {{ option.label }}
                </div>
              </div>
              <div>
                <h5 class="font-semibold mb-2">{{ i18n.t('marketplace.general.categories') }}</h5>
                <Treeselect v-model="activeCategory" :options="availableCategories" :multiple="false" :clearable="false" />
              </div>
              <div v-if="availableSubCategories.length">
                <h5 class="font-semibold mb-2">{{ i18n.t('marketplace.general.subcategories') }}</h5>
                <Treeselect v-model="activeSubCategory" :options="[{id: 'all', label: i18n.t('marketplace.items_index.all_subcategories')}].concat(availableSubCategories)" :multiple="false" :clearable="false" />
              </div>
              <button class="jt-btn jt-mk-btn mt-2" @click="applyFilters">{{ i18n.t('marketplace.general.confirm') }}</button>
              <div class="w-full py-2 flex items-center text-sm font-semibold justify-center underline cursor-pointer" @click="resetFilters">{{ i18n.t('marketplace.items_index.clear_filters') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="">
      <div class="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        <ItemCard v-for="item in items" :key="item.id" :item="item" :start_date="dates.start" :end_date="dates.end" />
      </div>
      <div v-if="loading || initialLoad" class="text-center py-4 flex items-center justify-center h-80">
        <i class="fa fa-spinner fa-spin"></i> {{ i18n.t('marketplace.general.loading') }}
      </div>
      <div v-if="!loading && items.length === 0 && !initialLoad" class="text-center py-4 flex items-center justify-center h-80 opacity-60">
        {{ i18n.t('marketplace.items_index.no_items_found') }}
      </div>
    </div>
  </div>
</template>
<script>

import SearchDestination from '../welcome/search_destination.vue';
import Treeselect from '@riophae/vue-treeselect';
import axios from '../../shared/custom_axios.js';
import ItemCard from './card.vue';

export default {
  name: 'ItemsIndex',
  components: {
    SearchDestination,
    Treeselect,
    ItemCard
  },
  props: {
    categories: {
      type: Array,
      required: true
    },
    subcategories: {
      type: Array,
      required: true
    },
    profile: {
      type: Object,
      required: false,
      default: null
    },
    cart: {
      type: Object,
      required: false,
      default: null
    }
  },
  computed: {
    availableSubCategories() {
      return this.subcategories
        .filter(sub => sub.cat_id == this.activeCategory)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    availableCategories() {
      return this.categories
        .filter(cat => this.subcategories.some(sub => sub.cat_id == cat.id))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    activeCategoryLabel() {
      const category = this.categories.find(cat => cat.id == this.activeCategory);
      return category ? category.label : '';
    }
  },
  watch: {
    availableCategories() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.checkCategoriesScroll();
        }, 100);
      });
    },
    availableSubCategories() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.checkSubcategoriesScroll();
        }, 100);
      });
    }
  },
  created() {
    // Initialize the active type to rentals by default
    const params = new URLSearchParams(window.location.search);
    this.location_name = params.get('location_name') || '';
    this.location_id = params.get('location_id') || null;
    this.specificItemId = params.get('item_id') || null;
    if (params.get('start_date')) {
      this.dates.start = new Date(params.get('start_date'));
    }
    if (params.get('end_date')) {
      this.dates.end = new Date(params.get('end_date'));
    }
    this.activeType = params.get('type') || 'rentals';
    this.activeCategory = params.get('category');
    this.activeSubCategory = params.get('subcategory') || 'all';
    if (!this.activeCategory || !this.availableCategories.some(cat => cat.id == this.activeCategory)) {
      this.activeCategory = this.availableCategories[0]?.id;
    }
    this.searchQuery = params.get('search') || '';
    this.activeSorting = params.get('sort') || 'for_you';
  },
  mounted() {
    this.getItems();
    this.setupInfiniteScroll();
    this.$nextTick(() => {
      // Add a small delay to ensure content is rendered
      setTimeout(() => {
        this.checkCategoriesScroll();
        this.checkSubcategoriesScroll();
      }, 100);
      window.addEventListener('resize', this.handleResize);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  data() {
    return {
      initialLoad: true,
      activeType: 'rentals', // Default active type
      activeCategory: null,
      activeSubCategory: 'all',
      searchQuery: '',
      openFilterFlyout: false,
      sorting: [
        { label: this.i18n.t('marketplace.items_index.for_you'), value: 'for_you' },
        { label: this.i18n.t('marketplace.items_index.ranking'), value: 'highest_rated' },
        { label: this.i18n.t('marketplace.items_index.lowest_price'), value: 'lowest_price' },
        { label: this.i18n.t('marketplace.items_index.highest_price'), value: 'highest_price' }
      ],
      activeSorting: 'for_you',
      location_name: '',
      location_id: null,
      dates: { start: null, end: null },
      page: 1,
      items: [],
      loading: false,
      lastPage: false,
      specificItemId: null,
      showingSingleItem: false,
      showLeftScroll: false,
      showRightScroll: false,
      showLeftScrollSub: false,
      showRightScrollSub: false,
    };
  },
  methods: {
    getCategoryIcon(categoryName) {
      // Map category names to Bootstrap Icons
      const iconMap = {
      };
      
      const normalizedName = categoryName.toLowerCase().trim();
      return iconMap[normalizedName] || 'bi-tag';
    },
    showAllRentals() {
      this.specificItemId = null;
      this.showingSingleItem = false;
      this.items = [];
      this.page = 1;
      this.lastPage = false;
      this.loading = false;
      
      // Update URL to remove item_id parameter
      const params = new URLSearchParams(window.location.search);
      params.delete('item_id');
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
      
      this.getItems();
    },
    clearCart() {
      // Clear the cart using an API call
      axios.delete('/marketplace/cart')
        .then(() => {
          window.location.reload();
        })
        .catch(error => {
          console.error('Error clearing cart:', error);
          // Reload anyway to ensure UI is in sync
          window.location.reload();
        });
    },
    openFilter() {
      // Logic to open filter modal or dropdown
      this.openFilterFlyout = true;
    },
    handleCategoriesScroll() {
      this.checkCategoriesScroll();
    },
    checkCategoriesScroll() {
      const container = this.$refs.categoriesContainer;
      if (!container) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = container;
      this.showLeftScroll = scrollLeft > 50;
      this.showRightScroll = (scrollLeft + clientWidth) < (scrollWidth - 10);
    },
    handleSubcategoriesScroll() {
      this.checkSubcategoriesScroll();
    },
    checkSubcategoriesScroll() {
      const container = this.$refs.subcategoriesContainer;
      if (!container) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = container;
      this.showLeftScrollSub = scrollLeft > 10;
      this.showRightScrollSub = (scrollLeft + clientWidth) < (scrollWidth - 10);
    },
    handleResize() {
      this.checkCategoriesScroll();
      this.checkSubcategoriesScroll();
    },
    scrollCategories(direction) {
      const container = this.$refs.categoriesContainer;
      if (!container) return;
      
      const scrollAmount = 300; // pixels to scroll
      const targetScroll = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    },
    scrollSubcategories(direction) {
      const container = this.$refs.subcategoriesContainer;
      if (!container) return;
      
      const scrollAmount = 200; // pixels to scroll
      const targetScroll = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    },
    setupInfiniteScroll() {
      window.addEventListener('scroll', this.handleScroll);
    },
    handleScroll() {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollPosition >= documentHeight - 100) {
        this.infiniteScroll();
      }
    },
    infiniteScroll() {
      // Logic to handle infinite scroll
      if (!this.loading && !this.lastPage) {
        this.loading = true;
        this.page++;
        this.getItems();
      }
    },
    resetFilters() {
      this.activeType = 'rentals';
      this.activeCategory = this.availableCategories[0]?.id;
      this.activeSubCategory = 'all';
      this.searchQuery = '';
      this.activeSorting = 'for_you';
      this.applyFilters();
    },
    setLocationAndDates(location, dates) {
      this.location_name = location.name;
      this.location_id = location.id;
      this.dates.start = dates.start;
      this.dates.end = dates.end;
      this.applyFilters();
    },
    applyFilters() {
      const params = new URLSearchParams();
      params.set('type', this.activeType);
      params.set('category', this.activeCategory);
      if (this.activeSubCategory !== 'all') {
        params.set('subcategory', this.activeSubCategory);
      }
      // Set location and date filters
      params.set('location_name', this.location_name);
      params.set('location_id', this.location_id || '');
      if (this.dates.start) {
        params.set('start_date', this.dates.start.toISOString());
      }
      if (this.dates.end) {
        params.set('end_date', this.dates.end.toISOString());
      }
      // Set search query and sorting
      params.set('search', this.searchQuery);
      params.set('sort', this.activeSorting);
      // Save current scroll position before reload
      sessionStorage.setItem('marketplace_scroll_position', window.scrollY);
      // Update the URL with the new query parameters
      window.location.href = `${window.location.pathname}?${params}`;
    },
    getItems() {
      // Fetch items based on the current filters
      const requestParams = {
        page: this.page,
        type: this.activeType,
        category: this.activeCategory,
        subcategory: this.activeSubCategory,
        location_name: this.location_name,
        location_id: this.location_id,
        start_date: this.dates.start ? this.dates.start.toISOString() : null,
        end_date: this.dates.end ? this.dates.end.toISOString() : null,
        search: this.searchQuery,
        sort: this.activeSorting
      };
      
      // If specificItemId is set and we haven't shown all items yet, fetch only that item
      if (this.specificItemId && !this.showingSingleItem) {
        requestParams.item_id = this.specificItemId;
      }
      
      axios.get('/marketplace/items', {
        params: requestParams
      })
      .then(response => {
        // Handle the response data
        if (response.data.data.length > 0) {
          this.items = [...this.items, ...response.data.data];
          // If we fetched a specific item, mark that we're showing a single item
          if (this.specificItemId && !this.showingSingleItem) {
            this.showingSingleItem = true;
          }
        } else {
          this.lastPage = true; // No more items to load
        }
        this.loading = false;
        this.initialLoad = false;
        
        // Restore scroll position after initial load
        if (this.page === 1) {
          this.$nextTick(() => {
            this.restoreScrollPosition();
          });
        }
      })
      .catch(error => {
        console.error('Error fetching items:', error);
        this.loading = false;
      });
    },
    restoreScrollPosition() {
      const savedPosition = sessionStorage.getItem('marketplace_scroll_position');
      if (savedPosition) {
        // Wait a bit for images and content to render
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedPosition, 10),
            behavior: 'instant'
          });
          // Clear the saved position after restoring
          sessionStorage.removeItem('marketplace_scroll_position');
        }, 100);
      }
    } 
  },
};
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Hide arrows on mobile, show on desktop */
.arrow-button {
  display: none;
}

@media (min-width: 768px) {
  .arrow-button {
    display: flex;
  }
}
</style>